import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { OnboardingData } from "@/types/onboarding";
import { generateSlug } from "@/lib/formatters";
import { validateRequired, validateDomainPrefix } from "@/lib/validation";
import { ArrowRight, Globe } from "lucide-react";

interface LibraryInfoStepProps {
  data: OnboardingData;
  onChange: (updates: Partial<OnboardingData>) => void;
  onNext: () => void;
}

export default function LibraryInfoStep({ data, onChange, onNext }: LibraryInfoStepProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (data.libraryName && !data.domainPrefix) {
      const slug = generateSlug(data.libraryName);
      onChange({ domainPrefix: slug });
    }
  }, [data.libraryName, data.domainPrefix, onChange]);

  const handleLibraryNameChange = (value: string) => {
    onChange({ libraryName: value });
    if (!data.domainPrefix || data.domainPrefix === generateSlug(data.libraryName)) {
      onChange({ domainPrefix: generateSlug(value) });
    }
    if (errors.libraryName) {
      setErrors((prev) => ({ ...prev, libraryName: "" }));
    }
  };

  const clearFieldError = (field: string) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleNext = () => {
    const newErrors: Record<string, string> = {};

    const nameValidation = validateRequired(data.libraryName, "Library name");
    if (!nameValidation.isValid) newErrors.libraryName = "Library name is required";

    const domainValidation = validateDomainPrefix(data.domainPrefix);
    if (!domainValidation.isValid)
      newErrors.domainPrefix = domainValidation.errors[0] || "Invalid domain prefix";

    const firstNameValidation = validateRequired(data.adminFirstName, "First name");
    if (!firstNameValidation.isValid) newErrors.adminFirstName = "First name is required";

    const lastNameValidation = validateRequired(data.adminLastName, "Last name");
    if (!lastNameValidation.isValid) newErrors.adminLastName = "Last name is required";

    if (!data.adminEmail) {
      newErrors.adminEmail = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.adminEmail)) {
      newErrors.adminEmail = "Please enter a valid email address";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onNext();
    } else {
      const firstErrorField = Object.keys(newErrors)[0];
      document.getElementById(firstErrorField)?.focus();
    }
  };

  return (
    <div className="space-y-6">
      {/* Library details section */}
      <fieldset className="space-y-4">
        <legend className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          Library Details
        </legend>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="libraryName">
              Library name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="libraryName"
              value={data.libraryName}
              onChange={(e) => handleLibraryNameChange(e.target.value)}
              placeholder="e.g., City Public Library"
              className={errors.libraryName ? "border-red-500 focus-visible:ring-red-500/20" : ""}
            />
            {errors.libraryName && (
              <p className="text-xs text-red-600" role="alert">
                {errors.libraryName}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="libraryType">Library type</Label>
            <Select
              value={data.libraryType}
              onValueChange={(value: OnboardingData["libraryType"]) =>
                onChange({ libraryType: value })
              }
            >
              <SelectTrigger id="libraryType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="university">University</SelectItem>
                <SelectItem value="public library">Public Library</SelectItem>
                <SelectItem value="school">School</SelectItem>
                <SelectItem value="corporate">Corporate</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="domainPrefix">
            Library URL <span className="text-red-500">*</span>
          </Label>
          <div className="flex items-center">
            <div className="flex items-center gap-2 px-3 h-10 bg-slate-50 border border-r-0 border-input rounded-l-md">
              <Globe className="h-4 w-4 text-slate-400" />
            </div>
            <Input
              id="domainPrefix"
              value={data.domainPrefix}
              onChange={(e) => {
                onChange({ domainPrefix: e.target.value });
                clearFieldError("domainPrefix");
              }}
              placeholder="your-library"
              className={`rounded-l-none border-l-0 ${errors.domainPrefix ? "border-red-500 focus-visible:ring-red-500/20" : ""}`}
            />
            <span className="px-3 h-10 flex items-center bg-slate-50 border border-l-0 border-input rounded-r-md text-sm text-slate-500 whitespace-nowrap">
              .philolms.com
            </span>
          </div>
          {errors.domainPrefix ? (
            <p className="text-xs text-red-600" role="alert">
              {errors.domainPrefix}
            </p>
          ) : (
            <p className="text-xs text-slate-400">This will be your library's web address</p>
          )}
        </div>
      </fieldset>

      {/* Section divider */}
      <div className="border-t border-slate-200" />

      {/* Admin details section */}
      <fieldset className="space-y-4">
        <legend className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          Administrator
        </legend>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="adminFirstName">
              First name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="adminFirstName"
              value={data.adminFirstName}
              onChange={(e) => {
                onChange({ adminFirstName: e.target.value });
                clearFieldError("adminFirstName");
              }}
              placeholder="Jane"
              className={
                errors.adminFirstName ? "border-red-500 focus-visible:ring-red-500/20" : ""
              }
            />
            {errors.adminFirstName && (
              <p className="text-xs text-red-600" role="alert">
                {errors.adminFirstName}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="adminLastName">
              Last name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="adminLastName"
              value={data.adminLastName}
              onChange={(e) => {
                onChange({ adminLastName: e.target.value });
                clearFieldError("adminLastName");
              }}
              placeholder="Smith"
              className={errors.adminLastName ? "border-red-500 focus-visible:ring-red-500/20" : ""}
            />
            {errors.adminLastName && (
              <p className="text-xs text-red-600" role="alert">
                {errors.adminLastName}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="adminEmail">
            Email address <span className="text-red-500">*</span>
          </Label>
          <Input
            id="adminEmail"
            type="email"
            value={data.adminEmail}
            onChange={(e) => {
              onChange({ adminEmail: e.target.value });
              clearFieldError("adminEmail");
            }}
            placeholder="admin@yourlibrary.edu"
            className={errors.adminEmail ? "border-red-500 focus-visible:ring-red-500/20" : ""}
          />
          {errors.adminEmail && (
            <p className="text-xs text-red-600" role="alert">
              {errors.adminEmail}
            </p>
          )}
        </div>
      </fieldset>

      {/* Navigation */}
      <div className="flex justify-end pt-2">
        <Button onClick={handleNext} size="lg">
          Continue
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
